import {
  Message,
  MessagesResponse,
  ToolCall,
  UpdateToolConfirmationPayload,
  UpdateToolConfirmationResponse,
} from "@cohesive/api-client";
import {
  InfiniteData,
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { orgAPI } from "./api";

export function useUpdateToolConfirmation(
  options?: UseMutationOptions<
    UpdateToolConfirmationResponse,
    AxiosError,
    {
      flowSessionId: string;
      flowMessageId: string;
      toolCallId: string;
      payload: UpdateToolConfirmationPayload;
    },
    {
      previousMessages?: InfiniteData<MessagesResponse>;
    }
  >,
) {
  const queryClient = useQueryClient();
  return useMutation<
    UpdateToolConfirmationResponse,
    AxiosError,
    {
      flowSessionId: string;
      flowMessageId: string;
      toolCallId: string;
      payload: UpdateToolConfirmationPayload;
    },
    {
      previousMessages?: InfiniteData<MessagesResponse>;
    }
  >({
    mutationFn: async ({ flowSessionId, flowMessageId, toolCallId, payload }) =>
      await orgAPI.toolConfirmations.update(
        flowSessionId,
        flowMessageId,
        toolCallId,
        payload,
      ),
    onMutate: async ({ flowSessionId, flowMessageId, toolCallId, payload }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ["flowMessages", flowSessionId],
      });

      // Snapshot the previous value
      const previousMessages = queryClient.getQueryData<
        InfiniteData<MessagesResponse>
      >(["flowMessages", flowSessionId]);

      // Optimistically update the message's tool call status
      queryClient.setQueryData<InfiniteData<MessagesResponse>>(
        ["flowMessages", flowSessionId],
        (old) => {
          if (!old) return old;

          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              data: {
                ...page.data,
                flow_messages: page.data.flow_messages.map(
                  (message: Message) => {
                    if (message.id !== flowMessageId) return message;

                    return {
                      ...message,
                      tool_calls: message.tool_calls?.map(
                        (toolCall: ToolCall) => {
                          if (toolCall.id !== toolCallId) return toolCall;
                          return {
                            ...toolCall,
                            user_confirmation_status:
                              payload.user_confirmation_status === "confirmed"
                                ? "confirmed-processing"
                                : "rejected",
                          };
                        },
                      ),
                    };
                  },
                ),
              },
            })),
          };
        },
      );

      return { previousMessages };
    },
    onError: (_, { flowSessionId }, context) => {
      // If the mutation fails, roll back to the previous value
      if (context?.previousMessages) {
        queryClient.setQueryData(
          ["flowMessages", flowSessionId],
          context.previousMessages,
        );
      }
    },
    onSuccess: (_, { flowSessionId }) => {
      queryClient.invalidateQueries({
        queryKey: ["flowMessages", flowSessionId],
      });
    },
    ...options,
  });
}
